'use strict';

import axios from 'axios';
import { Group, Matrix4 } from 'three';
import { ParksEnvironment } from './EnvironmentLayers/ParksEnvironment';
import { isMobile, projectMode, resourcesFolder, settingsData, threeJsVariables } from '../../../settings';
import { BaseElement } from '../../interfaces/BaseElement';
import { changeLangToCurrent, getCurrentLang } from '../langSwitcher/langSwitcher';
import { LeisureEnvironment } from './EnvironmentLayers/LeisureEnvironment';
import { EducationEnvironment } from './EnvironmentLayers/EducationEnvironment';
import { FunEnvironment } from './EnvironmentLayers/FunEnvironment';
import { RoadsEnvironment } from './EnvironmentLayers/RoadsEnvironment';
import { RailwayEnvironment } from './EnvironmentLayers/RailwayEnvironment';
import { TerrainEnvironment } from '../Terrain/Terrain';
import { Popup } from '../publicLayers/Popup';
import { buildingHoverOut } from '../../threejs/raycast';
import { Cloudflare } from '../../api/Cloudflare';
import { BuildingsEnvironment } from './EnvironmentLayers/BuildingEnvironment';
import { DayNightEnvironment } from './EnvironmentLayers/DayNightEnvironment';
import { Loader } from '../../threejs/loader/Loader';
import { SeaEnvironment } from './EnvironmentLayers/SeaEnvironment';
import { TreesEnvironment } from './EnvironmentLayers/TreesEnvironment';
import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';
import { BmbyMode } from '../../mode/BmbyMode';
import {lordIconColors} from "../../../lordicon";

const DEFAULT_SETTINGS = {
	active: false,
};

let intersectedEnvironment = null;
let environmentObject = null;

export class Environment {
	static scope = null;

	constructor () {
		if (config.features.environment) {
			this.params = config.features.environment;
		} else {
			this.params = DEFAULT_SETTINGS;
		}

		this.group = new Group();
		this.group.name = 'Environment';
		this.environments = {};
		this.lat = 0;
		this.lng = 0;
		this.activePublicLayer = null;
		this.bundleScene = null;
		this.bundleLoaded = false;
		this.popup = new Popup();
		Environment.scope = this;
	}

	load () {
		if (!this.active || BmbyMode.active) return;

		if (Environment.fromBundle()) {
			const loader = new Loader();
			const dracoLoader = loader.dracoLoader;
			dracoLoader.load(`${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/points/osm.glb?v=${Cloudflare.version}`, glb => {
				this.bundleScene = glb.scene;
				this.config = glb.scene.userData.config;
				this.#onLoad();
			});
		} else {
			axios.get(`${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/points/environment.json?v=${Cloudflare.version}`)
				.then(response => this.config = response.data)
				.catch(() => this.config = {})
				.finally(() => this.#onLoad());
		}
	}

	#onLoad () {
		if (!Object.keys(this.config).length) {
			this.params.active = false;
			return;
		}

		const root = Environment.fromBundle() ? this.config.Environment : this.config;

		this.lat = root.lat;
		this.lng = root.lng;
		const matrix = new Matrix4().fromArray(root.matrix);
		this.group.applyMatrix4(matrix);

		this.environments = {
			Terrain: new TerrainEnvironment(),
			Buildings: new BuildingsEnvironment(),
			DayNight: new DayNightEnvironment(),
			Sea: new SeaEnvironment(),
			Trees: new TreesEnvironment(),
			Highway: new RoadsEnvironment(),
			Railway: new RailwayEnvironment(),
			Parks: new ParksEnvironment(),
			Education: new EducationEnvironment(),
			Leisure: new LeisureEnvironment(),
			Fun: new FunEnvironment(),
		};
		
		if (projectMode.AirMode.active) {
			delete this.environments.Highway;
			delete this.environments.Railway;
			delete this.environments.Sea;
			delete this.environments.Terrain;
			delete this.environments.Trees;
			delete this.environments.Buildings;
		}

		Object.entries(this.environments).forEach(([name, scope]) => {
			this.group.add(scope.group);

			if (Environment.fromBundle()) {
				scope.loadFromBundle(this.bundleScene, this.config);
			} else {
				scope.settings = this.config[name];

				// if (name === 'Buildings' && this.config[name]) {
				// 	scope.load();
				// }

				if (name === 'DayNight' && this.config[name]) {
					scope.create();
				}

				// if (name === 'Terrain' && this.config[name]) {
				// 	scope.create();
				// }
			}
		});

		if (OrbitDayAndNight.isActiveAndSelected || (OrbitDayAndNight.isActive && projectMode.AirMode.active && !isMobile)) {
			Environment.toggle3D('show');
		}

		threeJsVariables.scene.add(this.group);
		this.bundleLoaded = true;
	}

	async createPublicLayers (container) {
		const publicLayerContainer = new BaseElement()
			.addClass('public-layers', 'display-option')
			.appendTo(container)
			.DOMElement;

		new BaseElement()
			.addClass('public-layers__toggle-block')
			.appendTo(publicLayerContainer)
			.DOMElement;

		// new BaseElement('span')
		// 	.addClass('public-layers__title', 'language-string')
		// 	.innerHtml(changeLangToCurrent('Public layers'))
		// 	.addAttribute('data-dictionary', 'Public layers')
		// 	.appendTo(toggleBlock);

		const layersContainer = new BaseElement()
			.addClass('public-layers__elements')
			.appendTo(publicLayerContainer)
			.event('click', this.publicLayersHandler.bind(this))
			.DOMElement;

		if (Environment.fromBundle()) {
			await new Promise(resolve => {
				const awaitBundleInterval = setInterval(() => {
					if (this.bundleLoaded) {
						clearInterval(awaitBundleInterval);
						resolve();
					}
				},100);
			});
		}

		Object.entries(this.environments).forEach(([name, scope]) => {
			if (name !== 'Terrain' && name !== 'Buildings' && name !== 'Sea' && name !== 'Trees') {
				scope.createPublicButton(layersContainer);
			}
		});
	}

	publicLayersHandler (e) {
		const button = e.target.closest('.public-layer__btn');
		if (!button)
			return;
		
		const id = button.getAttribute('data-public-layer');
		
		if (threeJsVariables.cssLabelManager.isBlocked(id) && e.action !== 'backButton')
			return;

		if (settingsData.isConferenceHost) {
			const dealsButtonElement = document.querySelector('.public-layer__btn.deals');
			// const {primaryColor, secondaryColor, activeColor} = getLordIconColor();

			if (!button.classList.contains('deals')) {
				playdownDeals(dealsButtonElement, lordIconColors.primary, lordIconColors.secondary);
			} else {
				const state = dealsButtonElement.getAttribute('state');
				if (state === 'pressed') {
					playdownDeals(dealsButtonElement, lordIconColors.primary, lordIconColors.secondary);
				} else {
					highlightDeals(dealsButtonElement, lordIconColors.active);

					const id = button.getAttribute('data-public-layer');
					const previouslyActiveLayer = Object.values(this.environments).find(scope => scope?.active);
					if (previouslyActiveLayer && previouslyActiveLayer.id !== id) {
						previouslyActiveLayer.publicButtonHandler();
					}
				}
			}
		}

		
		if (this.environments[id]) {
			const previouslyActiveLayer = Object.values(this.environments).find(scope => scope?.active);
			if (previouslyActiveLayer && previouslyActiveLayer.id !== id)
				previouslyActiveLayer.publicButtonHandler();
			
			this.environments[id].publicButtonHandler();
			this.activePublicLayer = this.environments[id];
			if (previouslyActiveLayer && previouslyActiveLayer.id === id)
				this.activePublicLayer = null;
		} else {
			this.activePublicLayer = null;
		}

		this.popup.hidePopup();

		function playdownDeals(dealsButtonElement, primaryColor, secondaryColor) {
			document.querySelectorAll('.deals-point').forEach(item => item.classList.add('hidden'));
			dealsButtonElement.setAttribute('state', 'non-pressed');

			const icon = dealsButtonElement.querySelector('.lord__icon');
			icon.setAttribute('colors',`primary:${primaryColor},secondary:${secondaryColor}`);
			dealsButtonElement.style.color = '';
		}

		function highlightDeals(dealsButtonElement, activeColor) {
			dealsButtonElement.setAttribute('state', 'pressed');
			document.querySelectorAll('.deals-point').forEach(item => item.classList.remove('hidden'));

			const icon = dealsButtonElement.querySelector('.lord__icon');
			icon.setAttribute('colors',`primary:${activeColor},secondary:${activeColor}`);
			dealsButtonElement.style.color = activeColor;
		}
	}

	addCustomLayer (key, layer) {
		this.environments[key] = layer;
	}

	getEnvironmentTransformation () {
		const matrix = new Matrix4();
		matrix.copy(this.group.matrixWorld);
		return matrix;
	}

	setDayTime(times) {
	    Object.values(this.environments).forEach(scope => {
	       if (scope.setDayTime) {
	           scope.setDayTime(times);
           }
        });
    }

    setTime(time) {
        Object.values(this.environments).forEach(scope => {
            if (scope.setTime) {
                scope.setTime(time);
            }
        });
    }

	setAlpha(alpha) {
		Object.values(this.environments).forEach(scope => {
			if (scope.setAlpha) {
				scope.setAlpha(alpha);
			}
		});
	}

	intersect (obj) {
		if (this.activePublicLayer && this.activePublicLayer.id === 'Highway') {
			switch (typeof obj) {
				case 'object':
					intersectedEnvironment = this.environments[obj.userData.environmentId];
					environmentObject = intersectedEnvironment.getObjectById(obj.userData.id);
					if (environmentObject.tags.hasOwnProperty('name')) {
						if (environmentObject.tags.hasOwnProperty(`name:${getCurrentLang()}`)) {
							this.popup.popupContent = environmentObject.tags[`name:${getCurrentLang()}`];
						} else {
							this.popup.popupContent = environmentObject.tags.name;
						}
						this.popup.showPopup();
						buildingHoverOut();
					} else {
						this.popup.hidePopup();
					}
					break;
				case 'string':
					this.popup.updatePopupPosition();
					break;
				case 'undefined':
					this.popup.hidePopup();
					intersectedEnvironment = null;
					environmentObject = null;
					break;
			}
		}
	}
	
	hideLabelsByDistance(state) {
		Object.values(this.environments).forEach(scope => {
			scope.hideLabelsByDistance(state);
		});
	}

	get active () {
		return this.params.active;
	}

	static setDayTime(times) {
	    Environment.scope?.setDayTime(times);
    }

    static setTime(time) {
	    Environment.scope?.setTime(time);
    }

    static setAlpha(alpha) {
		Environment.scope?.setAlpha(alpha);
    }

	static addCustomLayer (key, layer) {
		Environment.scope?.addCustomLayer(key, layer);
	}

	static addLayers (container) {
		Environment.scope?.createPublicLayers(container);
	}

	static isActive () {
		return Environment.scope?.params.active;
	}

	static getLayer (id) {
		return Environment.scope?.environments[id];
	}

	static getEnvironmentTransformation () {
		return Environment.scope?.getEnvironmentTransformation();
	}

	static setActivePublicLayer (scope) {
		if (Environment.scope) {
			Environment.scope.activePublicLayer = scope;
		}
	}

	static getActivePublicLayer () {
		return Environment.scope?.activePublicLayer;
	}

	static animate (delta) {
		if (!Environment.getActivePublicLayer()) return;
		Environment.getActivePublicLayer().tick(delta);
	}

	static hide () {
		if (Environment.scope?.activePublicLayer) {
			Environment.scope.activePublicLayer.hide();
		}
	}

	static show () {
		if (Environment.scope?.activePublicLayer) {
			Environment.scope.activePublicLayer.show();
		}
	}

	static mouseOverHandler () {
		if (Environment.scope?.activePublicLayer) {
			Environment.scope.activePublicLayer.mouseOverHandler();
		}
	}

	static mouseOutHandler () {
		if (Environment.scope?.activePublicLayer) {
			Environment.scope.activePublicLayer.mouseOutHandler();
		}
	}

	static intersect (obj) {
		Environment.scope?.intersect(obj);
	}

	static fromBundle () {
		return config.features.environment?.active && config.features.environment?.allInBundle;
	}

	static toggle3D (state) {
		if (Environment.scope && Environment.fromBundle()) {

			switch (state) {
				case 'show':
					Environment.scope.environments.DayNight.show();
					Environment.scope.environments.Terrain?.show();
					Environment.scope.environments.Buildings?.show();
					Environment.scope.environments.Trees?.show();
					Environment.scope.environments.Sea?.show();
					Environment.scope.hideLabelsByDistance(false);
					break;
				case 'hide':
					Environment.scope.environments.DayNight.hide();
					Environment.scope.environments.Terrain?.hide();
					Environment.scope.environments.Buildings?.hide();
					Environment.scope.environments.Trees?.hide();
					Environment.scope.environments.Sea?.hide();
					Environment.scope.hideLabelsByDistance(true);
					break;

			}
		}
	}


}

window.env = Environment;
